import React, { useMemo, useState, useCallback } from "react";

const HUBS = [
  { name: "New York",      x: 294, y: 145, color: "#4ad6ff",
    keywords: ["us","new york","fed","nasdaq","nyse","america","dollar","sec","crypto","s&p","dow","wall street","federal reserve","fomc","treasury"] },
  { name: "Washington DC", x: 280, y: 160, color: "#60a5fa",
    keywords: ["fed","fomc","powell","federal reserve","rate","congress","white house","sec","cftc","us policy","treasury","fiscal","debt ceiling"] },
  { name: "London",        x: 498, y: 113, color: "#4ade80",
    keywords: ["uk","london","europe","ecb","gbp","sterling","eu","regulation","ftse","bank of england","boe"] },
  { name: "Frankfurt",     x: 525, y: 125, color: "#a3e635",
    keywords: ["ecb","lagarde","eurozone","euro","frankfurt","german","dax","european central bank","eu economy"] },
  { name: "Tokyo",         x: 888, y: 160, color: "#f5b042",
    keywords: ["japan","tokyo","yen","nikkei","boj","jpy","asia","bank of japan","ueda"] },
  { name: "Singapore",     x: 788, y: 261, color: "#b377ff",
    keywords: ["singapore","binance","bybit","sea","southeast","mas"] },
  { name: "Dubai",         x: 652, y: 191, color: "#ff8a4d",
    keywords: ["dubai","uae","middle east","arabic","bahrain","saudi","opec","oil","aramco"] },
  { name: "Sydney",        x: 920, y: 364, color: "#5cf2ff",
    keywords: ["australia","sydney","aud","pacific","asx","reserve bank"] },
  { name: "São Paulo",     x: 370, y: 335, color: "#ff5cb4",
    keywords: ["brazil","latam","latin","south america","bovespa","real"] },
  { name: "Shanghai",      x: 837, y: 172, color: "#ff5d6c",
    keywords: ["china","chinese","yuan","hong kong","ban","beijing","pboc","ccp","trade war","tariff"] },
  { name: "Washington",    x: 275, y: 156, color: "#60a5fa",
    keywords: ["fed rate","fomc meeting","powell","debt","deficit","cpi","pce","nonfarm","jobs report"] },
];

// Dedup — Washington DC and Washington point same area; merge by keywords only
const DISPLAY_HUBS = HUBS.filter(h => h.name !== "Washington");

const CONTINENTS = [
  "104,62 158,46 208,56 252,72 292,92 322,142 318,196 292,238 258,262 222,264 185,250 162,206 147,156 122,102",
  "258,18 344,12 378,30 368,62 320,76 278,66 256,40",
  "246,268 302,252 350,265 372,295 366,380 318,440 278,446 240,416 224,368 226,310 238,275",
  "450,100 542,72 568,96 565,140 540,158 514,165 490,162 464,145 448,122",
  "500,48 526,42 546,52 540,90 520,96 500,82 492,62",
  "457,90 478,83 488,88 484,102 468,106 456,96",
  "462,172 562,154 596,192 598,282 558,374 514,408 474,400 450,318 448,204",
  "566,180 626,165 658,185 655,226 625,245 598,240 568,212",
  "648,196 698,188 722,216 718,262 685,282 655,268 638,233",
  "568,60 650,48 750,44 848,64 934,108 944,165 894,215 842,228 794,206 750,218 695,188 648,196 598,192 568,144 562,90",
  "750,218 800,204 822,226 818,260 795,278 768,275 748,252",
  "880,128 900,122 909,138 906,158 890,162 878,150 876,135",
  "782,310 878,292 940,315 942,388 890,415 824,415 784,378 780,344",
];

const CAT_COLOR = {
  CRYPTO:      "#4ad6ff",
  MACRO:       "#f5b042",
  FED:         "#ff8a4d",
  EQUITIES:    "#4ade80",
  COMMODITIES: "#d4a857",
  GEO:         "#ff5d6c",
  MARKETS:     "#b377ff",
};

function newsAgoShort(pub) {
  if (!pub) return "";
  const s = Math.floor((Date.now() - new Date(pub).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function mapNewsToHubs(news) {
  const result = {};
  DISPLAY_HUBS.forEach(h => { result[h.name] = []; });
  (news || []).forEach(item => {
    const txt = ((item.title || "") + " " + (item.source || "")).toLowerCase();
    let best = null, bestScore = 0;
    DISPLAY_HUBS.forEach(hub => {
      const score = hub.keywords.filter(kw => txt.includes(kw)).length;
      if (score > bestScore) { bestScore = score; best = hub.name; }
    });
    result[best || "New York"].push(item);
  });
  return result;
}

function CategoryPill({ cat }) {
  const color = CAT_COLOR[cat] || "#8a93a3";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "1px 5px", borderRadius: 3,
      background: `${color}18`, border: `1px solid ${color}44`,
      color, fontSize: 7, fontFamily: "ui-monospace,monospace",
      fontWeight: 700, letterSpacing: "0.1em", flexShrink: 0,
    }}>
      {cat}
    </span>
  );
}

export default function WorldNewsMap({ news = [] }) {
  const [activeHub, setActiveHub] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null); // category filter

  const newsMap = useMemo(() => mapNewsToHubs(news), [news]);

  const handleHubClick = useCallback((name) => {
    setActiveHub(v => v === name ? null : name);
  }, []);

  const activeHubData = DISPLAY_HUBS.find(h => h.name === activeHub);
  const activeNews = useMemo(() => {
    let items = activeHub ? (newsMap[activeHub] || []) : [];
    if (activeFilter) items = items.filter(n => n.category === activeFilter);
    return items;
  }, [activeHub, newsMap, activeFilter]);

  // Category counts for active hub
  const catCounts = useMemo(() => {
    if (!activeHub) return {};
    const counts = {};
    (newsMap[activeHub] || []).forEach(n => {
      const c = n.category || "CRYPTO";
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [activeHub, newsMap]);

  // Global stats
  const totalByCategory = useMemo(() => {
    const counts = {};
    (news || []).forEach(n => {
      const c = n.category || "CRYPTO";
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [news]);

  return (
    <div>
      {/* Category stats bar */}
      {news.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8,
          padding: "6px 8px",
          background: "rgba(255,255,255,0.02)",
          borderRadius: 6, border: "1px solid rgba(255,255,255,0.05)",
        }}>
          {Object.entries(totalByCategory).sort((a,b) => b[1]-a[1]).map(([cat, cnt]) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(f => f === cat ? null : cat)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                padding: "2px 6px", borderRadius: 4, cursor: "pointer",
                background: activeFilter === cat ? `${CAT_COLOR[cat] || "#8a93a3"}22` : "transparent",
                border: `1px solid ${activeFilter === cat ? (CAT_COLOR[cat] || "#8a93a3") : "rgba(255,255,255,0.1)"}`,
                color: CAT_COLOR[cat] || "#8a93a3",
                fontSize: 8, fontFamily: "ui-monospace,monospace", fontWeight: 700,
                letterSpacing: "0.1em",
              }}
            >
              {cat} <span style={{ color: "rgba(255,255,255,0.45)", fontWeight: 400 }}>{cnt}</span>
            </button>
          ))}
          {activeFilter && (
            <button
              onClick={() => setActiveFilter(null)}
              style={{
                padding: "2px 6px", borderRadius: 4, cursor: "pointer",
                background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
                color: "rgba(255,255,255,0.4)", fontSize: 8,
                fontFamily: "ui-monospace,monospace",
              }}
            >✕ clear</button>
          )}
        </div>
      )}

      <svg
        viewBox="0 0 1000 500"
        className="w-full block rounded-xl"
        style={{
          background: "linear-gradient(180deg, #020810 0%, #030d1a 100%)",
          border: "1px solid rgba(74,214,255,0.1)",
          cursor: "crosshair",
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {DISPLAY_HUBS.map(h => (
            <radialGradient key={`g-${h.name}`} id={`hubglow-${h.name.replace(/\s/g,"")}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={h.color} stopOpacity="0.5" />
              <stop offset="100%" stopColor={h.color} stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>

        {/* Grid */}
        {[1,2,3,4,5].map(i => (
          <line key={`h${i}`} x1="0" y1={i*83} x2="1000" y2={i*83} stroke="rgba(74,214,255,0.04)" strokeWidth="0.5" />
        ))}
        {[1,2,3,4,5,6,7,8,9,10,11].map(i => (
          <line key={`v${i}`} x1={i*83} y1="0" x2={i*83} y2="500" stroke="rgba(74,214,255,0.04)" strokeWidth="0.5" />
        ))}

        {/* Continents */}
        {CONTINENTS.map((pts, i) => (
          <polygon key={i} points={pts} fill="#0b1e30" stroke="rgba(74,214,255,0.13)" strokeWidth="0.6" />
        ))}

        {/* Hubs */}
        {DISPLAY_HUBS.map(hub => {
          const allNews = newsMap[hub.name] || [];
          const count = activeFilter
            ? allNews.filter(n => n.category === activeFilter).length
            : allNews.length;
          const isActive = activeHub === hub.name;
          return (
            <g key={hub.name} onClick={() => handleHubClick(hub.name)} style={{ cursor: "pointer" }}>
              {isActive && (
                <circle cx={hub.x} cy={hub.y} r="28"
                  fill={`url(#hubglow-${hub.name.replace(/\s/g,"")})`} />
              )}
              {count > 0 && (
                <>
                  <circle cx={hub.x} cy={hub.y} r="14" fill="none" stroke={hub.color} strokeWidth="1"
                    opacity="0.5" style={{ animation: "worldHubPulse 2.4s ease-out infinite" }} />
                  <circle cx={hub.x} cy={hub.y} r="22" fill="none" stroke={hub.color} strokeWidth="0.5"
                    opacity="0.25" style={{ animation: "worldHubPulse 2.4s ease-out 0.8s infinite" }} />
                </>
              )}
              <circle cx={hub.x} cy={hub.y} r={isActive ? 6 : 4.5} fill={hub.color}
                style={{ filter: `drop-shadow(0 0 ${isActive ? 10 : 6}px ${hub.color})`, transition: "r 200ms" }} />
              <circle cx={hub.x} cy={hub.y} r={isActive ? 2.5 : 2} fill="#fff" opacity="0.9" />
              {count > 0 && (
                <g>
                  <circle cx={hub.x+7} cy={hub.y-7} r="7" fill={hub.color} />
                  <text x={hub.x+7} y={hub.y-4} textAnchor="middle" fill="#000" fontSize="7" fontWeight="bold" fontFamily="ui-monospace,monospace">
                    {count}
                  </text>
                </g>
              )}
              <text
                x={hub.x + (hub.x > 800 ? -10 : 10)}
                y={hub.y + 1}
                textAnchor={hub.x > 800 ? "end" : "start"}
                fill={isActive ? hub.color : `${hub.color}88`}
                fontSize={isActive ? "8" : "7"}
                fontWeight={isActive ? "bold" : "normal"}
                fontFamily="ui-monospace,monospace"
                style={{ pointerEvents: "none", transition: "all 200ms" }}
              >
                {hub.name.toUpperCase()}
              </text>
            </g>
          );
        })}

        {/* Live label */}
        <text x="10" y="16" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="ui-monospace,monospace" letterSpacing="2">
          WORLD MARKET INTEL
        </text>
        <circle cx="114" cy="12" r="3" fill="#4ade80">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.6s" repeatCount="indefinite" />
        </circle>
      </svg>

      {/* News callout panel */}
      {activeHubData ? (
        <div className="mt-2 rounded-xl overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${activeHubData.color}08, rgba(0,0,0,0))`,
            border: `1px solid ${activeHubData.color}22`,
          }}>
          <div className="flex items-center justify-between px-3 py-2"
            style={{ borderBottom: `1px solid ${activeHubData.color}15` }}>
            <span className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold"
              style={{ color: activeHubData.color }}>
              {activeHubData.name}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {/* Category mini-filters for this hub */}
              {Object.entries(catCounts).map(([cat, cnt]) => (
                <button key={cat} onClick={() => setActiveFilter(f => f === cat ? null : cat)}
                  style={{
                    padding: "1px 4px", borderRadius: 3, cursor: "pointer",
                    background: activeFilter === cat ? `${CAT_COLOR[cat] || "#8a93a3"}22` : "transparent",
                    border: `1px solid ${CAT_COLOR[cat] || "#8a93a3"}55`,
                    color: CAT_COLOR[cat] || "#8a93a3",
                    fontSize: 7, fontFamily: "ui-monospace,monospace", fontWeight: 700,
                  }}>
                  {cat} {cnt}
                </button>
              ))}
              <span className="text-[9px] text-white/30 font-mono">
                {activeNews.length} item{activeNews.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
          {activeNews.length === 0 ? (
            <div className="px-3 py-3 text-[9px] text-white/30 font-mono">
              {activeFilter ? `No ${activeFilter} news for this region` : "No news mapped to this region"}
            </div>
          ) : (
            <div className="px-3 py-2 space-y-2">
              {activeNews.slice(0, 5).map((n, i) => {
                const hasUrl = !!n.url;
                const Tag = hasUrl ? "a" : "div";
                const linkProps = hasUrl ? {
                  href: n.url, target: "_blank", rel: "noopener noreferrer",
                } : {};
                return (
                  <Tag key={i} {...linkProps}
                    className={hasUrl ? "wmap-headline-link" : ""}
                    style={{
                      display: "flex", gap: 8, alignItems: "flex-start",
                      textDecoration: "none",
                      cursor: hasUrl ? "pointer" : "default",
                      borderRadius: 4, padding: "2px 4px", margin: "-2px -4px",
                      transition: "background 120ms",
                    }}>
                    <span style={{ fontSize: 8, fontFamily: "ui-monospace,monospace", marginTop: 2, flexShrink: 0, color: activeHubData.color }}>→</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 4, marginBottom: 2, flexWrap: "wrap" }}>
                        {n.category && <CategoryPill cat={n.category} />}
                        <p style={{ fontSize: 10, color: "rgba(255,255,255,0.75)", lineHeight: 1.4, margin: 0 }}>
                          {n.title?.length > 100 ? n.title.slice(0, 100) + "…" : n.title}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", fontFamily: "ui-monospace,monospace" }}>
                          {n.source}
                        </span>
                        {n.published && (
                          <span style={{ fontSize: 8, color: "rgba(255,255,255,0.2)", fontFamily: "ui-monospace,monospace" }}>
                            · {newsAgoShort(n.published)}
                          </span>
                        )}
                        {n.impact >= 70 && (
                          <span style={{ fontSize: 7, color: "#ff5d6c", fontFamily: "ui-monospace,monospace", fontWeight: 700 }}>
                            HIGH IMPACT
                          </span>
                        )}
                        {hasUrl && (
                          <span style={{ fontSize: 7, color: activeHubData.color, fontFamily: "ui-monospace,monospace", fontWeight: 700, marginLeft: "auto" }}>
                            ↗ READ
                          </span>
                        )}
                      </div>
                    </div>
                  </Tag>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-1.5 text-center text-[9px] text-white/20 font-mono py-1">
          tap a hub · {news.length} live headlines across {Object.keys(totalByCategory).length} categories
        </div>
      )}
    </div>
  );
}
